<template>
  <a @click="triggerSchedule">
    <slot>
      <MTooltip>
        <template v-slot:trigger>
          <MButton
            shape="circle"
            variant="neutral-lighter"
            class="mx-1"
            :shadow="false"
          >
            <MIcon name="calendar-alt" />
          </MButton>
        </template>
        {{ $tc('review') }} {{ $tc('schedule') }}
      </MTooltip>
    </slot>
    <FlotoDrawerForm
      :open="isDrawerOpen"
      class="flex"
      @cancel="cancelAction"
      @submit="handleSchedule"
    >
      <template v-slot:header>
        {{ $tc('review') }} {{ $tc('schedule') }} {{ $tc('for') }}
        {{ item.name }}
      </template>
      <div v-if="!editing && !scheduleValue.id">
        <MRow class="mb-4" :gutter="0">
          <MCol :size="12" class="text-right">
            <MButton variant="primary" @click="editing = true">
              {{ $t('create') }} {{ $t('schedule') }}
            </MButton>
          </MCol>
          <MCol :size="12" class="text-center mt-8">
            <FlotoNoData>
              <template v-slot:message>
                {{ $t('not_scheduled') }}
              </template>
            </FlotoNoData>
          </MCol>
        </MRow>
      </div>
      <div
        v-else
        class="p-4 border border-solid rounded border-neutral-lighter relative"
      >
        <MIcon
          v-if="editing"
          name="times"
          size="1x"
          class="md-text-lg text-neutral cursor-pointer"
          variant="default"
          :style="{ position: 'absolute', top: '5px', right: '10px' }"
          @click="cancelEditing"
        />
        <FlotoDeleteBtn
          v-if="!editing"
          :style="{
            color: '#f04e3e',
            position: 'absolute',
            top: '5px',
            right: '40px',
          }"
          :message="
            $t('confirm_delete_item', {
              item: `${$tc('review')} ${$tc('scheduler')}`,
            })
          "
          @confirm="deleteSchedule"
        />
        <MTooltip v-if="!editing">
          <template v-slot:trigger>
            <MIcon
              name="pencil"
              class="text-primary cursor-pointer"
              variant="default"
              :style="{ position: 'absolute', top: '9px', right: '20px' }"
              @click="editing = true"
            />
          </template>
          {{ $t('edit') }}
        </MTooltip>
        <MRow>
          <MCol :size="6">
            <FlotoFormItem
              :label="`${$t('schedule')} ${$t('type')}`"
              rules="required"
            >
              <FlotoDropdownPicker
                v-if="editing"
                v-model="scheduleValue.frequency"
                as-input
                :options="scheduleFrequencyOptions"
              />
              <span v-else>
                {{
                  scheduleValue.frequency
                    ? (
                        scheduleFrequencyOptions.find(
                          (o) => o.key === scheduleValue.frequency
                        ) || {}
                      ).text
                    : '---'
                }}
              </span>
            </FlotoFormItem>
          </MCol>
        </MRow>
        <OnceInput
          v-if="scheduleValue.frequency === 'once'"
          v-model="scheduleValue"
          :disabled="!editing"
        />
        <MonthlyInput
          v-if="scheduleValue.frequency === 'monthly'"
          v-model="scheduleValue"
          :disabled="!editing"
        />
        <MRow>
          <MCol :size="6">
            <FlotoFormItem
              :label="$t('grace_period')"
              rules="nonzero|required|max_value:365|min_value:1"
            >
              <MInputGroup v-if="editing" compact class="input-group-container">
                <div class="flex-1 mb-1">
                  <MInputNumber
                    id="grace-period-input"
                    v-model="scheduleValue.gracePeriod"
                    type="number"
                    :precision="0"
                  />
                </div>
                <div
                  class="px-2 mb-1 py-1 bg-neutral-lightest flex items-center justify-center text-neutral border-solid border-neutral-lighter border-l-0 border-t border-b border-r rounded rounded-tl-none rounded-bl-none"
                  style="display: inline-flex"
                >
                  {{ $tc('days', 2) }}
                </div>
              </MInputGroup>
              <span v-else>
                {{ scheduleValue.gracePeriod }} {{ $tc('days', 2) }}
              </span>
            </FlotoFormItem>
          </MCol>
        </MRow>
        <MRow>
          <MCol :size="12">
            <h6>{{ $tc('reviewer', 2) }}</h6>
            <MDivider class="mt-0" />
          </MCol>
        </MRow>
        <MRow>
          <MCol :size="12">
            <FlotoTagsPicker
              v-model="scheduleValue.emails"
              :title="$tc('email')"
              type="email"
              show-empty
              :disabled="!editing"
              class="mb-1"
            />
          </MCol>
        </MRow>
        <MRow>
          <MCol :size="12" class="mb-2">
            <MCheckboxGroup
              v-model="scheduleValue.recipientKeywords"
              class="w-full"
              :disabled="!editing"
            >
              <MRow
                v-for="option in recipientKeywordsOptions"
                :key="option.key"
                class="p-1"
              >
                <MCol>
                  <MCheckbox :value="option.key">
                    {{ option.text }}
                  </MCheckbox>
                </MCol>
              </MRow>
            </MCheckboxGroup>
          </MCol>
        </MRow>
        <MRow>
          <MCol :size="6">
            <FlotoFormItem :label="`${$tc('technician', 2)}`">
              <FlotoTechnicianPicker
                v-model="scheduleValue.technicianIds"
                as-input
                searchable
                multiple
                :placeholder="!editing ? '---' : undefined"
                :disabled="!editing"
                :as-tag="!editing"
              />
            </FlotoFormItem>
          </MCol>
          <MCol :size="6">
            <FlotoFormItem :label="`${$tc('technician')} ${$tc('group', 2)}`">
              <FlotoTechnicianGroupPicker
                v-model="scheduleValue.technicianGroupIds"
                as-inpt
                searchable
                multiple
                :placeholder="!editing ? '---' : undefined"
                :disabled="!editing"
                :as-tag="!editing"
              />
            </FlotoFormItem>
          </MCol>
        </MRow>
        <MRow>
          <MCol :size="6">
            <FlotoFormItem :label="`${$tc('requester', 2)}`">
              <FlotoRequesterPicker
                v-model="scheduleValue.requesterIds"
                as-input
                searchable
                multiple
                :placeholder="!editing ? '---' : undefined"
                :disabled="!editing"
                :as-tag="!editing"
              />
            </FlotoFormItem>
          </MCol>
          <MCol :size="6">
            <FlotoFormItem :label="`${$tc('requester')} ${$tc('group', 2)}`">
              <FlotoRequesterGroupPicker
                v-model="scheduleValue.requesterGroupIds"
                as-inpt
                searchable
                multiple
                :placeholder="!editing ? '---' : undefined"
                :disabled="!editing"
                :as-tag="!editing"
              />
            </FlotoFormItem>
          </MCol>
        </MRow>
      </div>
      <template v-slot:actions="{ hide, submit }">
        <MButton
          v-if="editing"
          class="mr-2"
          :loading="processing"
          @click="submit"
        >
          {{ $tc('save') }}
        </MButton>
        <MButton variant="default" @click="hide">
          {{ $t('cancel') }}
        </MButton>
      </template>
    </FlotoDrawerForm>
  </a>
</template>
<script>
import CloneDeep from 'lodash/cloneDeep'
import MonthlyInput from '@components/schedule/monthly-input'
import OnceInput from '@components/schedule/once-input'

import {
  getKbReviewScheduleApi,
  createKbReviewScheduleApi,
  updateKbReviewScheduleApi,
  deleteKbReviewScheduleApi,
} from '../knowledge-api'
export default {
  name: 'ScheduleDrawer',
  components: { MonthlyInput, OnceInput },
  props: {
    item: { type: Object, required: true },
    resourceId: { type: Number, required: true },
  },
  data() {
    return {
      isDrawerOpen: false,
      processing: false,
      editing: false,
      scheduleValue: { parentId: this.resourceId, gracePeriod: 1 },
      unTouchedData: {},
    }
  },
  computed: {
    scheduleFrequencyOptions() {
      return [
        { text: this.$tc('once'), key: 'once' },
        { text: this.$t('monthly'), key: 'monthly' },
      ]
    },
    recipientKeywordsOptions() {
      return [
        {
          text: this.$tc('knowledge_folder_owner'),
          key: '{#knowledge.folder.owner.email#}',
        },
        {
          text: this.$tc('knowledge_author'),
          key: '{#knowledge.author.email#}',
        },
      ]
    },
  },
  methods: {
    cancelEditing() {
      this.editing = false
      this.scheduleValue = CloneDeep(this.unTouchedData)
    },
    getKbReviewSchedule() {
      return getKbReviewScheduleApi(this.resourceId).then((data) => {
        this.scheduleValue = { ...data, parentId: this.resourceId }
        this.unTouchedData = { ...data, parentId: this.resourceId }
      })
    },
    createKbReviewSchedule(item) {
      this.processing = true
      return createKbReviewScheduleApi(this.resourceId, item)
        .then((data) => {
          this.scheduleValue = data
          this.editing = false
          this.cancelAction()
          this.$emit('refresh')
        })
        .finally(() => (this.processing = false))
    },
    updateKbReviewSchedule(item) {
      this.processing = true
      return updateKbReviewScheduleApi(this.resourceId, item)
        .then((data) => {
          this.scheduleValue = data
          this.editing = true
          this.cancelAction()
          this.$emit('refresh')
        })
        .finally(() => (this.processing = false))
    },
    deleteKbReviewSchedule(id) {
      return deleteKbReviewScheduleApi(id).then(() => {
        this.cancelAction()
        this.$emit('refresh')
      })
    },
    triggerSchedule() {
      this.$emit('trigger')
      setTimeout(() => {
        this.isDrawerOpen = true
        this.getKbReviewSchedule()
      }, 400)
    },
    cancelAction() {
      this.isDrawerOpen = false
    },
    deleteSchedule(id) {
      this.deleteKbReviewSchedule(this.scheduleValue.parentId)
    },
    handleSchedule(data) {
      if (this.scheduleValue.id) {
        this.updateKbReviewSchedule(this.scheduleValue)
      } else {
        this.createKbReviewSchedule(this.scheduleValue)
      }
    },
  },
}
</script>
